import { useLocation, useNavigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  PenLine,
  Repeat2,
  ImageIcon,
  Bookmark,
  BotMessageSquare,
} from "lucide-react";
import type { StudioSection } from "@/components/StudioSidebar";

export type StudioActiveTab = "studio" | "saved" | "ai-coach";

interface BottomTabBarProps {
  /** When omitted (Settings, AICoach pages), tabs navigate back to /?section=<id>. */
  section?: StudioSection;
  onSelectSection?: (section: StudioSection) => void;
}

interface TabDef {
  id: StudioSection;
  label: string;
  icon: LucideIcon;
}

const TABS: TabDef[] = [
  { id: "rewrite", label: "Rewrite", icon: PenLine },
  { id: "repurpose", label: "Repurpose", icon: Repeat2 },
  { id: "images", label: "Images", icon: ImageIcon },
  { id: "workspace", label: "Workspace", icon: Bookmark },
  { id: "ai-coach", label: "AI Coach", icon: BotMessageSquare },
];

const BottomTabBar = ({ section, onSelectSection }: BottomTabBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = (tab: TabDef) => {
    if (onSelectSection && location.pathname === "/") {
      onSelectSection(tab.id);
      return;
    }
    navigate(`/?section=${tab.id}`);
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 flex items-stretch justify-around border-t border-[rgba(15,23,42,0.08)] bg-white lg:hidden"
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        minHeight: "calc(44px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      {TABS.map((tab) => {
        const active = section === tab.id;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleClick(tab)}
            className="relative flex min-h-[44px] min-w-[44px] flex-1 flex-col items-center justify-center gap-0.5 transition-colors max-[360px]:gap-0"
            style={{
              color: active ? "#6366F1" : "#94A3B8",
              WebkitTapHighlightColor: "transparent",
            }}
            aria-label={tab.label}
            aria-current={active ? "page" : undefined}
          >
            {active && (
              <span
                className="absolute left-2 right-2 top-0 h-0.5 rounded-full"
                style={{ background: "linear-gradient(90deg, #6366F1, #3B82F6)" }}
                aria-hidden
              />
            )}
            <Icon
              size={22}
              strokeWidth={active ? 2.2 : 1.8}
              style={{ color: active ? "#6366F1" : "#94A3B8" }}
            />
            <span
              className="max-[360px]:sr-only text-[11px] leading-tight"
              style={{
                fontWeight: active ? 600 : 400,
                color: active ? "#6366F1" : "#475569",
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomTabBar;
